import {
  useCallback,
  useEffect, useState,
} from 'react';
import {
  Map,
  AdvancedMarker,
  Pin,
  useApiIsLoaded,
  useMap,
} from '@vis.gl/react-google-maps';
import { useRecoilValue } from 'recoil';
import * as turf from '@turf/turf';
import PlaceHolder from '@src/modules/performance/components/Placeholder';
import { selectedDatabaseIdState, selectedStateHighwaysSubnetworkState } from '@transport-insights/uikit';
import { useConfig } from '@src/context/config';

import { useMapData } from '@src/modules/performance/context/map-api-hooks';
import networkData from '../../../shared/networkData';

import {
  mapActivityToText,
  mapCoassuranceToText,
  mapCoInvestorLabel,
  mapServicePerformanceToText,
} from '../shared/IndicatorText';

import { getNetworkNameFromId, getSubnetworkNameFromId } from '../../../shared/networkNames';
import { calculateActivityColor, mapValueToColourRange, servicePerformanceColours } from '../../../shared/helpers';
import GeoJsonLayer from './GeoJsonLayer';
import PopupBox from './PopupBox';

const getLatLonCenterFromGeom = (geometry) => {
  if (!geometry) return null;
  const centroid = turf.pointOnFeature(geometry);
  return {
    lat: centroid.geometry.coordinates[1],
    lng: centroid.geometry.coordinates[0],
  };
};

// Component to control map center
// This ensures the map pans to the new center when it changes
function MapCenterController({ center }) {
  const map = useMap();

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
    }
  }, [map, center]);

  return null;
}

function RCAMap({ currentSelection, filter }) {
  const config = useConfig();
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const isRegionOrNational = rcaId >= 1000;
  const isStateHighways = rcaId === 81;
  const useRcaData = (currentSelection === 'servicePerformance' || currentSelection === 'coassurance' || currentSelection === 'activity') && isRegionOrNational;
  const [networkType, setNetworkType] = useState('councils');
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const isLoaded = useApiIsLoaded();
  // state for the map center
  const [center, setCenter] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);

  useEffect(() => {
    if (!rcaId) return;
    // set the center based on the geometry of the selected rcaId
    if (rcaId === 1000 || rcaId === 88 || (isStateHighways && selectedSubnetwork === '')) {
      // if National, DOC road or State Highways with no subnetwork selected
      setCenter({ lat: -41.2865, lng: 174.7762 }); // default to Wellington
    } else if (isRegionOrNational) {
      setCenter(getLatLonCenterFromGeom(networkData.regions[rcaId]?.geometry) || null);
    } else if (isStateHighways) {
      setCenter(getLatLonCenterFromGeom(networkData.stateHighways[selectedSubnetwork]?.geometry) || null);
    } else {
      setCenter(getLatLonCenterFromGeom(networkData.councils[rcaId]?.geometry) || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcaId, selectedSubnetwork]);

  const { data: mapData } = useMapData(currentSelection, filter, networkType);
  const isLoading = !isLoaded || !rcaId || !mapData;

  useEffect(() => {
    if (useRcaData) {
      setNetworkType('councils');
    } else if (isRegionOrNational) {
      setNetworkType('regions');
    } else if (isStateHighways) {
      setNetworkType('stateHighways');
    } else {
      setNetworkType('councils');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcaId, currentSelection, filter]);

  // map the network kpi values to nice values shown in map indicators
  const getValuesForInfobox = useCallback((networkId) => {
    const result = {
      value: '',
      subtext: null,
    };

    const data = mapData?.[networkId];
    if (!data) {
      result.value = 'No data';
      return result;
    }

    switch (currentSelection) {
      case 'safety':
        if (data.value != null) {
          result.value = data.value;
          result.subtext = `(${data.totalCrashes} crashes)`;
        } else {
          result.value = 'No data';
        }
        break;

      case 'amenityCondition':
        if (data.value != null) {
          result.value = `${data.value.toFixed(1)}%`;
        } else {
          result.value = 'No data';
        }
        break;

      case 'activity':
        result.value = mapActivityToText(data.value, filter);
        result.subtext = data.subText ?? null;
        break;

      case 'coassurance':
        if (data.score != null) {
          result.value = mapCoassuranceToText(data.score);
          if (data.score > 0) {
            result.subtext = `${mapCoInvestorLabel(data.isTechnical)} ${data.auditDate}`;
          }
        } else {
          result.value = 'Not Available';
        }
        break;

      case 'servicePerformance':
        result.value = mapServicePerformanceToText(data.value);
        result.subtext = data.year ?? null;
        break;

      case 'dataQuality':
        if (data.value != null) {
          result.value = `${data.value}%`;
        } else {
          result.value = 'No data';
        }
        break;

      default:
        result.value = 'No data';
        break;
    }

    return result;
  }, [mapData, currentSelection, filter]);

  const calculateColorKey = useCallback((networkId) => {
    const data = mapData?.[networkId];
    if (!data) return null;

    switch (currentSelection) {
      case 'safety':
      case 'amenityCondition':
        return (data?.colour && data?.colour !== 'none') ? data.colour : null;

      case 'coassurance':
        return (data?.score != null)
          ? mapValueToColourRange(data.score, 'coassurance')
          : null;

      case 'servicePerformance':
        return servicePerformanceColours?.[data.value] ?? null;

      case 'activity':
        return calculateActivityColor?.(data.value, filter) ?? null;

      case 'dataQuality':
        return (data?.value != null)
          ? mapValueToColourRange(data.value, 'dataQuality')
          : null;

      default:
        return null;
    }
  }, [mapData, currentSelection, filter]);

  const handleMouseOver = useCallback((networkName, valueInfo) => {
    setHoverInfo({
      isVisible: true,
      networkName,
      valueInfo,
      currentSelection,
    });
  }, [currentSelection]);

  const handleMouseOut = useCallback(() => {
    setHoverInfo(null);
  }, []);

  if (isLoading) {
    return (
      <PlaceHolder height="100%" width="100%" />
    );
  }

  return (
    <Map
      defaultCenter={center}
      defaultZoom={6}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        fullscreenControl: false,
        draggable: true,
        scrollwheel: true,
      }}
      mapId={config?.GOOGLE_MAP_ID}
    >
      <MapCenterController center={center} />
      {center && rcaId !== 1000 && (!isStateHighways || (isStateHighways && typeof selectedSubnetwork === 'number')) && (
        <AdvancedMarker
          position={center}
        >
          <Pin background="white" borderColor="white" glyphColor="gray" />
        </AdvancedMarker>
      )}
      { /* Child components, such as markers, info windows, etc. */ }
      {Object.keys(networkData[networkType]).map((networkId) => {
        const colourKey = calculateColorKey(networkId);

        const networkName = isStateHighways && !useRcaData
          ? getSubnetworkNameFromId(networkId)
          : getNetworkNameFromId(networkId);

        const valueInfo = getValuesForInfobox(networkId);
        return (
          <GeoJsonLayer
            key={`${networkId}-${valueInfo.value}`}
            geoJson={networkData[networkType][networkId]}
            colourKey={colourKey}
            hoverEnabled
            filter={filter}
            currentSelection={currentSelection}
            onMouseOver={() => handleMouseOver(networkName, valueInfo)}
            onMouseOut={handleMouseOut}
          />
        );
      })}
      {hoverInfo && (
        <PopupBox
          isVisible
          title={hoverInfo.networkName}
          value={hoverInfo.valueInfo.value}
          fontSize={
            ['safety', 'amenityCondition', 'dataQuality'].includes(hoverInfo.currentSelection)
              ? 22
              : 12
          }
          subtext={hoverInfo.valueInfo.subtext}
        />
      )}
      {useRcaData && (isRegionOrNational || isStateHighways) && Object.keys(networkData.regions).map((network) => (
        <GeoJsonLayer
          key={network}
          geoJson={networkData.regions[network]}
          hoverEnabled={false}
          baseStyle={{
            clickable: false,
            fillColor: 'transparent',
            fillOpacity: 0.0,
            strokeColor: '#ffffff',
            strokeWeight: 1.25,
            zIndex: 20,
          }}
        />
      ))}
    </Map>
  );
}

export default RCAMap;
